import {
  PERCENTAGE_BASE,
  RATING_CONVERSION_FACTOR,
  PERFORMANCE_WEIGHT_RATING,
  PERFORMANCE_WEIGHT_SUCCESS_RATE,
  PERFORMANCE_WEIGHT_FEEDBACK_COVERAGE,
  PERFORMANCE_WEIGHT_USAGE,
  PERFORMANCE_WEIGHT_SUCCESS_ONLY,
  FEEDBACK_DISTRIBUTION_HIGH_RATING,
  FEEDBACK_DISTRIBUTION_MEDIUM_RATING,
  FEEDBACK_DISTRIBUTION_LOW_RATING,
  FEEDBACK_THRESHOLD_HIGH,
  FEEDBACK_THRESHOLD_LOW,
  DECIMAL_PRECISION_COST,
  DECIMAL_PRECISION_PERCENTAGE,
  DECIMAL_PRECISION_TIME,
  DECIMAL_PRECISION_RATING,
  DECIMAL_PRECISION_SCORE,
} from '../../../../config/constants';

/**
 * Transform stats response to template performance format
 */
export const transformStatsToTemplatePerformance = statsResponse => {
  if (!statsResponse?.success) return null;

  const templates = statsResponse.templates || [];
  return {
    success: true,
    templates: templates.map(t => ({
      template_id: t.template_id,
      template_name: t.template_name,
      category: t.template_category,
      execution_count: parseInt(t.total_executions) || 0,
      success_rate: parseFloat(t.success_rate_percentage) || 0,
      average_execution_time: parseFloat(t.avg_execution_time) || 0,
      total_cost: parseFloat(t.total_cost) || 0,
      avg_cost_per_execution: parseFloat(t.avg_cost) || 0,
      total_feedback: parseInt(t.total_feedback_count) || 0,
      avg_rating: parseFloat(t.avg_rating) || 0,
      model_config: t.model_config,
      created_at: t.created_at,
      updated_at: t.updated_at,
    })),
    total_templates: templates.length,
    total_executions: templates.reduce(
      (sum, t) => sum + (parseInt(t.total_executions) || 0),
      0
    ),
    total_cost: templates.reduce(
      (sum, t) => sum + (parseFloat(t.total_cost) || 0),
      0
    ),
    period: 'All Time',
    period_start: null,
    period_end: null,
    message: `Template performance (all-time) - ${templates.length} templates`,
  };
};

/**
 * Transform stats response to overview format
 */
export const transformStatsToOverview = statsResponse => {
  if (!statsResponse?.success) return null;

  const templates = statsResponse.templates || [];
  const totalExecutions = templates.reduce(
    (sum, t) => sum + (parseInt(t.total_executions) || 0),
    0
  );
  const successfulExecutions = templates.reduce(
    (sum, t) => sum + (parseInt(t.successful_executions) || 0),
    0
  );
  const totalCost = templates.reduce(
    (sum, t) => sum + (parseFloat(t.total_cost) || 0),
    0
  );
  const totalExecutionTime = templates.reduce(
    (sum, t) =>
      sum +
      (parseFloat(t.avg_execution_time) || 0) *
        (parseInt(t.total_executions) || 0),
    0
  );
  const activeTemplates = templates.filter(
    t => (parseInt(t.total_executions) || 0) > 0
  ).length;

  return {
    success: true,
    total_templates: templates.length,
    active_templates: activeTemplates,
    completed_executions: totalExecutions,
    total_cost: totalCost,
    success_rate:
      totalExecutions > 0
        ? (successfulExecutions / totalExecutions) * PERCENTAGE_BASE
        : 0,
    average_execution_time:
      totalExecutions > 0 ? totalExecutionTime / totalExecutions : 0,
    status_counts: {
      active: activeTemplates,
      draft: templates.length - activeTemplates,
    },
    period: 'All Time',
  };
};

/**
 * Transform cost breakdown to cost comparison format
 */
export const transformCostBreakdownToCostComparison = costBreakdownModels => {
  if (!costBreakdownModels || !Array.isArray(costBreakdownModels)) {
    return null;
  }

  return {
    success: true,
    cost_comparison: costBreakdownModels
      .filter(m => {
        const name = (m.model_name || '').toLowerCase();
        return (
          name.includes('gpt-4') ||
          name.includes('gpt4') ||
          name.includes('gpt-3.5') ||
          name.includes('gpt3.5')
        );
      })
      .map(m => ({
        model_name: m.model_name,
        executions: m.execution_count,
        total_cost: m.total_cost,
        avg_cost_per_execution:
          m.execution_count > 0
            ? parseFloat(
                (m.total_cost / m.execution_count).toFixed(
                  DECIMAL_PRECISION_COST
                )
              )
            : 0,
        success_rate: m.success_rate,
        avg_execution_time: 0,
      }))
      .sort((a, b) => {
        const aIs4 =
          a.model_name.toLowerCase().includes('gpt-4') ||
          a.model_name.toLowerCase().includes('gpt4');
        const bIs4 =
          b.model_name.toLowerCase().includes('gpt-4') ||
          b.model_name.toLowerCase().includes('gpt4');
        if (aIs4 && !bIs4) return -1;
        if (!aIs4 && bIs4) return 1;
        return b.executions - a.executions;
      })
      .slice(0, 2),
    total_executions: costBreakdownModels
      .filter(m => {
        const name = (m.model_name || '').toLowerCase();
        return (
          name.includes('gpt-4') ||
          name.includes('gpt4') ||
          name.includes('gpt-3.5') ||
          name.includes('gpt3.5')
        );
      })
      .reduce((sum, m) => sum + m.execution_count, 0),
    period: 'All Time',
  };
};

/**
 * Transform cost breakdown to model usage distribution format
 */
export const transformCostBreakdownToModelUsage = costBreakdownModels => {
  if (!costBreakdownModels || !Array.isArray(costBreakdownModels)) {
    return null;
  }

  const totalExecutionsForUsage = costBreakdownModels.reduce(
    (sum, m) => sum + (m.execution_count || 0),
    0
  );

  return {
    success: true,
    model_distribution: costBreakdownModels
      .map(m => ({
        model_name: m.model_name,
        execution_count: m.execution_count,
        successful_executions: Math.round(
          ((m.success_rate || 0) / PERCENTAGE_BASE) * m.execution_count
        ),
        failed_executions:
          m.execution_count -
          Math.round(
            ((m.success_rate || 0) / PERCENTAGE_BASE) * m.execution_count
          ),
        success_rate: m.success_rate || 0,
        total_cost: m.total_cost,
        avg_cost_per_execution:
          m.execution_count > 0
            ? parseFloat(
                (m.total_cost / m.execution_count).toFixed(
                  DECIMAL_PRECISION_COST
                )
              )
            : 0,
        avg_execution_time: 0,
        usage_percentage:
          totalExecutionsForUsage > 0
            ? parseFloat(
                (
                  (m.execution_count / totalExecutionsForUsage) *
                  PERCENTAGE_BASE
                ).toFixed(DECIMAL_PRECISION_PERCENTAGE)
              )
            : 0,
        templates_count: 1,
        categories: [],
      }))
      .sort((a, b) => b.execution_count - a.execution_count),
    total_executions: totalExecutionsForUsage,
    period: 'All Time',
  };
};

/**
 * Transform stats to category analytics format
 */
export const transformStatsToCategoryAnalytics = statsResponse => {
  if (!statsResponse?.success) return null;

  const templates = statsResponse.templates || [];
  const categoryStats = {};

  templates.forEach(t => {
    const category = t.template_category || 'uncategorized';
    const executions = parseInt(t.total_executions) || 0;
    const successfulExecutions = parseInt(t.successful_executions) || 0;
    const cost = parseFloat(t.total_cost) || 0;
    const avgExecTime = parseFloat(t.avg_execution_time) || 0;
    const feedbackCount = parseInt(t.total_feedback_count) || 0;
    const avgRating = parseFloat(t.avg_rating) || 0;

    if (!categoryStats[category]) {
      categoryStats[category] = {
        category: category,
        template_count: 0,
        total_executions: 0,
        successful_executions: 0,
        total_cost: 0,
        total_execution_time: 0,
        total_feedback: 0,
        rating_sum: 0,
        templates_with_feedback: 0,
      };
    }

    categoryStats[category].template_count += 1;
    categoryStats[category].total_executions += executions;
    categoryStats[category].successful_executions += successfulExecutions;
    categoryStats[category].total_cost += cost;
    categoryStats[category].total_execution_time += avgExecTime * executions;
    categoryStats[category].total_feedback += feedbackCount;

    if (feedbackCount > 0 && avgRating > 0) {
      categoryStats[category].rating_sum += avgRating * feedbackCount;
      categoryStats[category].templates_with_feedback += 1;
    }
  });

  return {
    success: true,
    category_analytics: Object.values(categoryStats)
      .map(cat => {
        const successRate =
          cat.total_executions > 0
            ? parseFloat(
                (
                  (cat.successful_executions / cat.total_executions) *
                  PERCENTAGE_BASE
                ).toFixed(DECIMAL_PRECISION_PERCENTAGE)
              )
            : 0;

        const avgExecutionTime =
          cat.total_executions > 0
            ? parseFloat(
                (cat.total_execution_time / cat.total_executions).toFixed(
                  DECIMAL_PRECISION_TIME
                )
              )
            : 0;

        const avgRatingForCategory =
          cat.total_feedback > 0
            ? parseFloat(
                (cat.rating_sum / cat.total_feedback).toFixed(
                  DECIMAL_PRECISION_RATING
                )
              )
            : 0;

        let goodFeedback = 0;
        let badFeedback = 0;
        if (cat.total_feedback > 0) {
          if (avgRatingForCategory >= 4.0) {
            goodFeedback = Math.round(
              cat.total_feedback * FEEDBACK_DISTRIBUTION_HIGH_RATING.good
            );
            badFeedback = Math.round(
              cat.total_feedback * FEEDBACK_DISTRIBUTION_HIGH_RATING.bad
            );
          } else if (avgRatingForCategory >= 3.0) {
            goodFeedback = Math.round(
              cat.total_feedback * FEEDBACK_DISTRIBUTION_MEDIUM_RATING.good
            );
            badFeedback = Math.round(
              cat.total_feedback * FEEDBACK_DISTRIBUTION_MEDIUM_RATING.bad
            );
          } else {
            goodFeedback = Math.round(
              cat.total_feedback * FEEDBACK_DISTRIBUTION_LOW_RATING.good
            );
            badFeedback = Math.round(
              cat.total_feedback * FEEDBACK_DISTRIBUTION_LOW_RATING.bad
            );
          }
        }

        return {
          category: cat.category,
          template_count: cat.template_count,
          total_executions: cat.total_executions,
          successful_executions: cat.successful_executions,
          success_rate: successRate,
          total_cost: parseFloat(
            cat.total_cost.toFixed(DECIMAL_PRECISION_COST)
          ),
          avg_execution_time: avgExecutionTime,
          total_feedback: cat.total_feedback,
          good_feedback: goodFeedback,
          bad_feedback: badFeedback,
          avg_success_rate: successRate,
        };
      })
      .sort((a, b) => b.template_count - a.template_count),
    total_categories: Object.keys(categoryStats).length,
    total_templates: templates.length,
    period: 'All Time',
    period_start: null,
    period_end: null,
    message: 'Category analytics (all-time)',
  };
};

/**
 * Transform stats to top performers format
 */
export const transformStatsToTopPerformers = statsResponse => {
  if (!statsResponse?.success) return null;

  const templates = statsResponse.templates || [];
  const templatePerformances = templates
    .filter(t => (parseInt(t.total_executions) || 0) > 0)
    .map(t => {
      const executionCount = parseInt(t.total_executions) || 0;
      const successfulExecutions = parseInt(t.successful_executions) || 0;
      const successRate =
        executionCount > 0
          ? (successfulExecutions / executionCount) * PERCENTAGE_BASE
          : 0;
      const avgRating = parseFloat(t.avg_rating) || 0;
      const totalFeedback = parseInt(t.total_feedback_count) || 0;
      const totalCost = parseFloat(t.total_cost) || 0;
      const avgCostPerExecution =
        executionCount > 0 ? totalCost / executionCount : 0;

      let performanceScore = 0;
      if (totalFeedback === 0) {
        performanceScore = successRate * PERFORMANCE_WEIGHT_SUCCESS_ONLY;
      } else {
        const ratingScore = avgRating * RATING_CONVERSION_FACTOR;
        const feedbackCoverage = Math.min(
          (totalFeedback / executionCount) * PERCENTAGE_BASE,
          PERCENTAGE_BASE
        );

        performanceScore =
          ratingScore * PERFORMANCE_WEIGHT_RATING +
          successRate * PERFORMANCE_WEIGHT_SUCCESS_RATE +
          feedbackCoverage * PERFORMANCE_WEIGHT_FEEDBACK_COVERAGE +
          Math.min(executionCount * 2, PERCENTAGE_BASE) *
            PERFORMANCE_WEIGHT_USAGE;
      }

      return {
        template_id: t.template_id,
        template_name: t.template_name,
        execution_count: executionCount,
        success_rate: parseFloat(
          successRate.toFixed(DECIMAL_PRECISION_PERCENTAGE)
        ),
        average_execution_time: parseFloat(t.avg_execution_time) || 0,
        total_cost: parseFloat(totalCost.toFixed(DECIMAL_PRECISION_TIME)),
        avg_cost_per_execution: parseFloat(
          avgCostPerExecution.toFixed(DECIMAL_PRECISION_COST)
        ),
        good_responses:
          avgRating >= 4
            ? Math.round(totalFeedback * FEEDBACK_THRESHOLD_HIGH)
            : Math.round(totalFeedback * (1 - FEEDBACK_THRESHOLD_HIGH)),
        bad_responses:
          avgRating <= 2
            ? Math.round(totalFeedback * FEEDBACK_THRESHOLD_HIGH)
            : Math.round(totalFeedback * FEEDBACK_THRESHOLD_LOW),
        total_feedback: totalFeedback,
        avg_rating: parseFloat(avgRating.toFixed(DECIMAL_PRECISION_RATING)),
        performance_score: parseFloat(
          performanceScore.toFixed(DECIMAL_PRECISION_SCORE)
        ),
      };
    })
    .sort((a, b) => {
      if (b.performance_score !== a.performance_score) {
        return b.performance_score - a.performance_score;
      }
      if (b.total_feedback !== a.total_feedback) {
        return b.total_feedback - a.total_feedback;
      }
      return b.avg_rating - a.avg_rating;
    });

  const templatesWithRatings = templatePerformances.filter(
    t => t.total_feedback > 0
  );
  const templatesWithoutRatings = templatePerformances.filter(
    t => t.total_feedback === 0
  );

  let bestTemplates;
  if (templatesWithRatings.length >= 5) {
    bestTemplates = templatesWithRatings.slice(0, 5);
  } else {
    bestTemplates = templatesWithRatings.slice();
    const remaining = 5 - bestTemplates.length;
    if (remaining > 0) {
      bestTemplates.push(...templatesWithoutRatings.slice(0, remaining));
    }
  }

  return {
    success: true,
    best_templates: bestTemplates,
    worst_templates: [],
    recent_activity: [],
    period: 'All Time',
    total_templates: templatePerformances.length,
    total_executions: templatePerformances.reduce(
      (sum, t) => sum + t.execution_count,
      0
    ),
    message: 'Top performers (all-time, rating-based)',
  };
};
